import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import BarChartIcon from "@redotech/redo-icons/bar-chart-12.svg";
import LineChartIcon from "@redotech/redo-icons/line-chart-up-01_filled.svg";
import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartType,
  DefaultDataPoint,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { RedoButton } from "../../buttons/redo-button";
import { defaultValueFormatter, getChartOptions } from "./chart-options";
import { crosshairPlugin } from "./crosshair-plugin";
import { ChartLegend } from "./legend";
import * as redoLineAndBarChartCss from "./redo-line-and-bar-chart.module.css";
import {
  BackgroundZonesPluginOptions,
  BackgroundZonesZoneConfig,
  ChartProcessedData,
  ChartReferenceLine,
  RedoChartStyle,
  RedoLineAndBarChartProps,
} from "./types";
import { useChartData } from "./useChartData";

type ChartJSOrUndefined<
  TType extends ChartType = ChartType,
  TData = DefaultDataPoint<TType>,
  TLabel = unknown,
> = ChartJS<TType, TData, TLabel> | undefined;

function findClosestIndex(refTime: number, times: number[]): number {
  let closestIndex = -1;
  let closestDiff = Infinity;
  for (const [i, time] of times.entries()) {
    const diff = Math.abs(time - refTime);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = i;
    }
  }
  return closestIndex;
}

function createReferenceLinesPlugin(
  referenceLines: ChartReferenceLine[] | undefined,
  seriesTimes: number[],
) {
  if (!referenceLines?.length || seriesTimes.length === 0) {
    return null;
  }

  return {
    id: "referenceLinesPlugin",
    afterDraw(chart: ChartJS) {
      for (const [index, line] of referenceLines.entries()) {
        const refTime = line.date.epochMilliseconds;
        const closestIndex = findClosestIndex(refTime, seriesTimes);
        if (closestIndex !== -1) {
          drawReferenceLine(chart, closestIndex, line, index);
        }
      }
    },
  };
}

interface ZonePixelBand {
  xStart: number;
  xEnd: number;
  zone: BackgroundZonesZoneConfig;
}

/**
 * Reads its config from `options.plugins.backgroundZones` (not a closure)
 * so that react-chartjs-2's options-update path carries zone/series changes
 * to an already-mounted chart — inline `plugins` arrays are only read at
 * chart construction.
 */
function resolveZoneBands(chart: ChartJS): ZonePixelBand[] {
  // chart.js deep-partials plugin option types; RedoLineAndBarChart always
  // writes the complete object, so narrow it back to the real shape.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const config = chart.options.plugins?.backgroundZones as
    | BackgroundZonesPluginOptions
    | undefined;
  const { chartArea, scales } = chart;
  // The plugin is registered globally, so it runs for every chart in the
  // app. Charts without backgroundZones get a resolver-created options
  // object with no keys — both inner optional chains are load-bearing.
  if (!config?.zones?.length || !config.seriesTimes?.length) {
    return [];
  }
  if (!chartArea || !scales.x) {
    return [];
  }
  const { zones, seriesTimes } = config;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const firstTime = seriesTimes[0]!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const lastTime = seriesTimes[seriesTimes.length - 1]!;
  // Half the distance between adjacent points, so zone edges land on the
  // boundaries between data points rather than on the points.
  const halfStep =
    seriesTimes.length > 1
      ? (scales.x.getPixelForValue(1) - scales.x.getPixelForValue(0)) / 2
      : (chartArea.right - chartArea.left) / 2;

  const bands: ZonePixelBand[] = [];
  for (const zone of zones) {
    if (zone.endMs < firstTime || zone.startMs > lastTime) {
      continue;
    }
    const startIndex = findClosestIndex(zone.startMs, seriesTimes);
    const endIndex = findClosestIndex(zone.endMs, seriesTimes);
    if (startIndex === -1 || endIndex === -1) {
      continue;
    }
    const xStart = Math.max(
      chartArea.left,
      scales.x.getPixelForValue(startIndex) - halfStep,
    );
    const xEnd = Math.min(
      chartArea.right,
      scales.x.getPixelForValue(endIndex) + halfStep,
    );
    if (xEnd <= xStart) {
      continue;
    }
    bands.push({ xStart, xEnd, zone });
  }
  return bands;
}

const backgroundZonesPlugin = {
  id: "backgroundZones",
  // Fills go under the data...
  beforeDatasetsDraw(chart: ChartJS) {
    const { ctx, chartArea } = chart;
    for (const { xStart, xEnd, zone } of resolveZoneBands(chart)) {
      if (!zone.fill) {
        continue;
      }
      ctx.save();
      ctx.fillStyle = zone.fill;
      ctx.fillRect(
        xStart,
        chartArea.top,
        xEnd - xStart,
        chartArea.bottom - chartArea.top,
      );
      ctx.restore();
    }
  },
  // ...labels go over it, so a data point at the top of the axis cannot
  // occlude them.
  afterDatasetsDraw(chart: ChartJS) {
    const { ctx, chartArea } = chart;
    for (const { xStart, xEnd, zone } of resolveZoneBands(chart)) {
      if (!zone.label) {
        continue;
      }
      ctx.save();
      ctx.fillStyle = zone.labelColor;
      ctx.font = "11px sans-serif";
      ctx.textAlign = "center";
      const labelWidth = ctx.measureText(zone.label).width;
      if (labelWidth + 8 <= xEnd - xStart) {
        ctx.fillText(zone.label, (xStart + xEnd) / 2, chartArea.top + 14);
      }
      ctx.restore();
    }
  },
};

function drawReferenceLine(
  chart: ChartJS,
  labelIndex: number,
  referenceLine: ChartReferenceLine,
  lineIndex: number,
) {
  const { ctx, chartArea, scales } = chart;
  if (!chartArea || !scales.x) {
    return;
  }

  const x = scales.x.getPixelForValue(labelIndex);
  const { top, bottom } = chartArea;

  ctx.save();
  ctx.beginPath();
  ctx.setLineDash(referenceLine.dashPattern ?? [5, 5]);
  ctx.strokeStyle = referenceLine.color ?? "#dc2626";
  ctx.lineWidth = 2;
  ctx.moveTo(x, top);
  ctx.lineTo(x, bottom);
  ctx.stroke();
  ctx.restore();

  if (referenceLine.label) {
    ctx.save();
    ctx.fillStyle = referenceLine.color ?? "#dc2626";
    ctx.font = "11px sans-serif";
    ctx.textAlign = "left";
    const yOffset = 14 + lineIndex * 14;
    ctx.fillText(referenceLine.label, x + 4, top + yOffset);
    ctx.restore();
  }
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  crosshairPlugin,
  backgroundZonesPlugin,
);

let lineBarChartMountCount = 0;

const loadingData: ChartProcessedData = {
  labels: [""],
  datasets: [
    {
      label: "",
      data: [0],
      borderColor: "transparent",
      backgroundColor: "transparent",
    },
  ],
  normalizedSeries: [],
};

export const RedoLineAndBarChart = memo(function RedoLineAndBarChart({
  series,
  leftAxisLabel,
  bottomAxisLabel,
  chartStyle: initialChartStyle = "line",
  legendPosition,
  dateFormatter,
  valueAxisFormatter = defaultValueFormatter,
  valueTooltipFormatter = defaultValueFormatter,
  titleText,
  allowGraphTypeSwitch = false,
  showTooltipSeriesNames = false,
  customTooltip,
  customTooltipTitles,
  loading = false,
  referenceLines,
  backgroundZones,
  showPoints = true,
  disableGradient = false,
  leftAxisPrecision,
  leftAxisMin,
  leftAxisMax,
  emphasizeZeroLine = false,
  lineTension,
  solidFill = false,
  onPointClick,
  "data-dd-action-name": actionName,
}: RedoLineAndBarChartProps) {
  const { i18n, t } = useTranslation();
  const resolvedDateFormatter = useMemo(
    () =>
      dateFormatter ??
      ((date: Temporal.Instant) =>
        date
          .toZonedDateTimeISO("UTC")
          .toLocaleString(i18n.language, { day: "numeric", month: "short" })),
    [dateFormatter, i18n.language],
  );
  const [internalChartStyle, setInternalChartStyle] =
    useState<RedoChartStyle>(initialChartStyle);
  const [hoveredTooltip, setHoveredTooltip] = useState<{
    content: string;
    x: number;
    y: number;
  } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const chartRef = useRef<ChartJSOrUndefined<"line" | "bar">>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const chartStyle = allowGraphTypeSwitch
    ? internalChartStyle
    : initialChartStyle;

  const actualProcessedData = useChartData(
    series,
    chartStyle,
    resolvedDateFormatter,
    disableGradient,
    showPoints,
    lineTension,
    solidFill,
  );

  const processedData = loading ? loadingData : actualProcessedData;

  const seriesTimes = useMemo(
    () =>
      (processedData.normalizedSeries[0]?.data ?? []).map(
        (d) => d.date.epochMilliseconds,
      ),
    [processedData.normalizedSeries],
  );

  const referenceLinesPlugin = useMemo(
    () => createReferenceLinesPlugin(referenceLines, seriesTimes),
    [referenceLines, seriesTimes],
  );

  // Absent values become concrete ("transparent"/"") because chart.js's
  // plugin-options proxy throws when resolving undefined keys. The label
  // default is a literal (gray-600) — canvas fillStyle cannot consume CSS
  // custom properties.
  const backgroundZonesOptions = useMemo(
    () =>
      backgroundZones?.length
        ? {
            zones: backgroundZones.map((zone) => ({
              startMs: zone.startDate.epochMilliseconds,
              endMs: zone.endDate.epochMilliseconds,
              fill: zone.fill ?? "transparent",
              label: zone.label ?? "",
              labelColor: zone.labelColor ?? "#4b5563",
            })),
            seriesTimes,
          }
        : undefined,
    [backgroundZones, seriesTimes],
  );

  useEffect(() => {
    if (!hoveredTooltip || !tooltipRef.current) {
      setTooltipPosition(null);
      return;
    }

    const tooltipWidth = tooltipRef.current.offsetWidth;
    const offset = 10;
    const viewportWidth = window.innerWidth;

    let left = hoveredTooltip.x + offset;

    if (left + tooltipWidth > viewportWidth - offset) {
      left = hoveredTooltip.x - tooltipWidth - offset;
    }

    setTooltipPosition({ left, top: hoveredTooltip.y - 10 });
  }, [hoveredTooltip]);

  useEffect(() => {
    lineBarChartMountCount++;
    return () => {
      lineBarChartMountCount--;
      if (lineBarChartMountCount === 0) {
        const tooltipEl = document.getElementById("chartjs-tooltip");
        if (tooltipEl) {
          tooltipEl.style.opacity = "0";
        }
      }
    };
  }, []);

  const plugins = useMemo(
    () => (referenceLinesPlugin ? [referenceLinesPlugin] : []),
    [referenceLinesPlugin],
  );

  const handleMouseMove = useHandler(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const chart = chartRef.current;
      if (!chart || !referenceLines?.length || seriesTimes.length === 0) {
        setHoveredTooltip(null);
        return;
      }

      const { chartArea, scales } = chart;
      if (!chartArea || !scales.x) {
        setHoveredTooltip(null);
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const hitThreshold = 10;

      for (const line of referenceLines) {
        const refTime = line.date.epochMilliseconds;
        const closestIndex = findClosestIndex(refTime, seriesTimes);
        if (closestIndex === -1) {
          continue;
        }

        const lineX = scales.x.getPixelForValue(closestIndex);
        if (Math.abs(mouseX - lineX) <= hitThreshold && line.tooltip) {
          setHoveredTooltip({
            content: line.tooltip,
            x: event.clientX,
            y: event.clientY,
          });
          return;
        }
      }

      setHoveredTooltip(null);
    },
  );

  const handleMouseLeave = useHandler(() => {
    setHoveredTooltip(null);
  });

  const baseOptions = getChartOptions({
    leftAxisLabel,
    bottomAxisLabel,
    valueAxisFormatter,
    valueTooltipFormatter,
    chartStyle,
    showSeriesNames: showTooltipSeriesNames,
    customTooltip,
    series: processedData.normalizedSeries,
    customTooltipTitles,
    leftAxisPrecision,
    leftAxisMin,
    leftAxisMax,
    emphasizeZeroLine,
    onPointClick,
  });

  const options = {
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      backgroundZones: backgroundZonesOptions,
    },
    maintainAspectRatio: false,
    responsive: true,
    ...(loading && {
      scales: {
        ...baseOptions.scales,
        y: { ...baseOptions.scales?.y, max: 100 },
      },
    }),
  };

  return (
    <Flex
      data-arbiter="RedoLineAndBarChart"
      data-dd-action-name={actionName}
      dir="column"
      height="full"
      minH="none"
      minW="none"
      w="full"
    >
      {(titleText || allowGraphTypeSwitch || legendPosition === "top") && (
        <Flex
          align="center"
          justify={titleText ? "space-between" : "flex-end"}
          mb="md"
          w="full"
        >
          {titleText && (
            <Text fontSize="sm" fontWeight="medium" textColor="secondary">
              {titleText}
            </Text>
          )}

          <Flex>
            {legendPosition === "top" && (
              <ChartLegend
                position={legendPosition}
                series={processedData.normalizedSeries}
              />
            )}
            {allowGraphTypeSwitch && (
              <Flex gap="xs">
                <RedoButton
                  aria-label={t("Line chart", { context: "chart type" })}
                  buttonClassName={
                    chartStyle === "line"
                      ? redoLineAndBarChartCss.chartControlButtonActive
                      : ""
                  }
                  hierarchy="tertiary"
                  IconTrailing={LineChartIcon}
                  onClick={() => setInternalChartStyle("line")}
                  size="sm"
                />
                <RedoButton
                  aria-label={t("Bar chart", { context: "chart type" })}
                  buttonClassName={
                    chartStyle === "bar"
                      ? redoLineAndBarChartCss.chartControlButtonActive
                      : ""
                  }
                  hierarchy="tertiary"
                  IconTrailing={BarChartIcon}
                  onClick={() => setInternalChartStyle("bar")}
                  size="sm"
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      )}

      <Flex grow={1} height="full" minH="none" minW="none" w="full">
        <Flex
          className={redoLineAndBarChartCss.chartContainer}
          flex="1 1 auto"
          height="full"
          maxW="full"
          minW="none"
          overflow="hidden"
          position="relative"
          w="full"
        >
          <Flex
            as="div"
            gap="none"
            height="full"
            minH="none"
            minW="none"
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
            w="full"
          >
            {chartStyle === "line" ? (
              <Line
                data={processedData as ChartData<"line">}
                options={options}
                plugins={plugins}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                ref={chartRef as React.Ref<ChartJSOrUndefined<"line">>}
              />
            ) : (
              <Bar
                data={processedData as ChartData<"bar">}
                options={options}
                plugins={plugins}
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                ref={chartRef as React.Ref<ChartJSOrUndefined<"bar">>}
              />
            )}
          </Flex>
          {hoveredTooltip && (
            <div
              className={redoLineAndBarChartCss.referenceLineTooltip}
              ref={tooltipRef}
              style={{
                position: "fixed",
                left: tooltipPosition?.left ?? hoveredTooltip.x + 10,
                top: tooltipPosition?.top ?? hoveredTooltip.y - 10,
                visibility: tooltipPosition ? "visible" : "hidden",
              }}
            >
              <Text fontSize="xs">{hoveredTooltip.content}</Text>
            </div>
          )}
        </Flex>

        {legendPosition === "right" && (
          <ChartLegend
            position={legendPosition}
            series={processedData.normalizedSeries}
          />
        )}
      </Flex>

      {legendPosition === "bottom" && (
        <ChartLegend
          position={legendPosition}
          series={processedData.normalizedSeries}
        />
      )}
    </Flex>
  );
});
